/**
 * Gráficos do backoffice — SVG à mão, sem biblioteca.
 *
 * Todos são de uma série só, na cor da marca: o título diz o que está
 * plotado, então não há legenda. Linhas de 2 px, área a ~10 %, grade em
 * fio de 1 px, barras com ponta arredondada e no máximo 24 px. A dica
 * (tooltip) só complementa: o valor também está nos números ao lado ou
 * na tabela.
 */
import { useCallback, useId, useLayoutEffect, useRef, useState, type ReactNode } from 'react';
import { niceTicks } from './chart-scale';
import { cn } from './cn';

/** Largura real do contêiner — o SVG desenha em pixels, sem esticar traço. */
function useWidth<T extends HTMLElement>() {
  const ref = useRef<T>(null);
  const [width, setWidth] = useState(0);
  useLayoutEffect(() => {
    const el = ref.current;
    if (!el) return;
    const ro = new ResizeObserver(([entry]) => setWidth(Math.floor(entry!.contentRect.width)));
    ro.observe(el);
    return () => ro.disconnect();
  }, []);
  return [ref, width] as const;
}

const PAD = { top: 12, right: 12, bottom: 26, left: 44 };

export interface SeriesPoint {
  /** Rótulo do eixo X e da dica. */
  label: string;
  value: number;
  /** Rótulo longo da dica (ex.: a data por extenso). */
  detail?: string;
}

interface AreaProps {
  points: SeriesPoint[];
  height?: number;
  formatY?: (v: number) => string;
  /** Texto da dica: valor em destaque, rótulo embaixo. */
  formatTooltip?: (p: SeriesPoint) => { value: string; label: string };
  /** Teto do eixo Y (ex.: 100 para percentuais). */
  yMax?: number;
  /** Faixas destacadas no eixo X, em índices de `points`. */
  highlights?: { from: number; to: number }[];
  /** Quantos rótulos no eixo X, no máximo. */
  xLabels?: number;
  ariaLabel: string;
}

/** Linha com área — série no tempo (visualizações por dia, curva de retenção). */
export function AreaChart({
  points,
  height = 240,
  formatY = (v) => v.toLocaleString('pt-BR'),
  formatTooltip,
  yMax,
  highlights = [],
  xLabels = 6,
  ariaLabel,
}: AreaProps) {
  const [ref, width] = useWidth<HTMLDivElement>();
  const [hover, setHover] = useState<number | null>(null);
  const gradient = useId();

  const w = Math.max(width - PAD.left - PAD.right, 1);
  const h = height - PAD.top - PAD.bottom;
  const ticks = niceTicks(yMax ?? Math.max(...points.map((p) => p.value), 0));
  const top = yMax ?? ticks[ticks.length - 1]!;
  const n = points.length;
  const x = (i: number) => PAD.left + (n <= 1 ? w / 2 : (i / (n - 1)) * w);
  const y = (v: number) => PAD.top + h - (Math.min(v, top) / (top || 1)) * h;

  const line = points.map((p, i) => `${i ? 'L' : 'M'}${x(i).toFixed(1)},${y(p.value).toFixed(1)}`).join('');
  const area = n ? `${line}L${x(n - 1).toFixed(1)},${PAD.top + h}L${x(0).toFixed(1)},${PAD.top + h}Z` : '';
  const labelEvery = Math.max(1, Math.ceil(n / xLabels));

  const onMove = useCallback(
    (e: React.PointerEvent<SVGRectElement>) => {
      const box = e.currentTarget.getBoundingClientRect();
      const rel = (e.clientX - box.left) / box.width;
      setHover(Math.round(rel * (n - 1)));
    },
    [n],
  );

  const tip =
    hover !== null && points[hover]
      ? (formatTooltip?.(points[hover]!) ?? {
          value: formatY(points[hover]!.value),
          label: points[hover]!.detail ?? points[hover]!.label,
        })
      : null;

  return (
    <div ref={ref} className="relative select-none" style={{ height }}>
      {width > 0 && (
        <svg width={width} height={height} role="img" aria-label={ariaLabel} className="overflow-visible">
          <defs>
            <linearGradient id={gradient} x1="0" x2="0" y1="0" y2="1">
              <stop offset="0%" stopColor="var(--color-brand-400)" stopOpacity="0.22" />
              <stop offset="100%" stopColor="var(--color-brand-400)" stopOpacity="0.02" />
            </linearGradient>
          </defs>

          {highlights.map((r, i) => (
            <rect
              key={i}
              x={x(r.from)}
              y={PAD.top}
              width={Math.max(x(r.to) - x(r.from), 2)}
              height={h}
              fill="var(--color-fg)"
              opacity="0.05"
            />
          ))}

          {ticks.map((t) => (
            <g key={t}>
              <line x1={PAD.left} x2={PAD.left + w} y1={y(t)} y2={y(t)} stroke="var(--color-line)" />
              <text
                x={PAD.left - 8}
                y={y(t)}
                dy="0.32em"
                textAnchor="end"
                className="fill-fg-subtle text-[11px] tabular-nums"
              >
                {formatY(t)}
              </text>
            </g>
          ))}

          {points.map((p, i) => {
            // O último rótulo aparece se não colar no anterior.
            const last = i === n - 1 && (n - 1) % labelEvery >= labelEvery / 2;
            if (i % labelEvery !== 0 && !last) return null;
            return (
              <text
                key={i}
                x={x(i)}
                y={height - 6}
                textAnchor={i === 0 ? 'start' : i === n - 1 ? 'end' : 'middle'}
                className="fill-fg-subtle text-[11px]"
              >
                {p.label}
              </text>
            );
          })}

          <path d={area} fill={`url(#${gradient})`} />
          <path
            d={line}
            fill="none"
            stroke="var(--color-brand-400)"
            strokeWidth="2"
            strokeLinejoin="round"
            strokeLinecap="round"
          />

          {hover !== null && points[hover] && (
            <g pointerEvents="none">
              <line
                x1={x(hover)}
                x2={x(hover)}
                y1={PAD.top}
                y2={PAD.top + h}
                stroke="var(--color-line-strong)"
              />
              <circle
                cx={x(hover)}
                cy={y(points[hover]!.value)}
                r="4.5"
                fill="var(--color-brand-400)"
                stroke="var(--color-surface)"
                strokeWidth="2"
              />
            </g>
          )}

          <rect
            x={PAD.left}
            y={PAD.top}
            width={w}
            height={h}
            fill="transparent"
            onPointerMove={onMove}
            onPointerLeave={() => setHover(null)}
          />
        </svg>
      )}

      {tip && hover !== null && (
        <Tooltip x={x(hover)} width={width}>
          <span className="block text-sm font-semibold tabular-nums">{tip.value}</span>
          <span className="block text-xs text-fg-subtle">{tip.label}</span>
        </Tooltip>
      )}
    </div>
  );
}

interface ColumnProps {
  items: { label: string; value: number; tooltip?: string }[];
  height?: number;
  formatY?: (v: number) => string;
  /** Mostra só alguns rótulos no eixo X (ex.: a cada 3 horas). */
  labelEvery?: number;
  ariaLabel: string;
}

/** Colunas — distribuição por categoria ordenada (hora do dia, dia da semana). */
export function ColumnChart({
  items,
  height = 180,
  formatY = (v) => String(v),
  labelEvery = 1,
  ariaLabel,
}: ColumnProps) {
  const [ref, width] = useWidth<HTMLDivElement>();
  const [hover, setHover] = useState<number | null>(null);
  const pad = { ...PAD, left: 32 };
  const w = Math.max(width - pad.left - pad.right, 1);
  const h = height - pad.top - pad.bottom;
  const ticks = niceTicks(Math.max(...items.map((i) => i.value), 0), 3);
  const top = ticks[ticks.length - 1] || 1;
  const band = w / Math.max(items.length, 1);
  const bar = Math.min(24, Math.max(band - 2, 2));
  const y = (v: number) => pad.top + h - (v / top) * h;

  return (
    <div ref={ref} className="relative select-none" style={{ height }}>
      {width > 0 && (
        <svg width={width} height={height} role="img" aria-label={ariaLabel}>
          {ticks.map((t) => (
            <g key={t}>
              <line x1={pad.left} x2={pad.left + w} y1={y(t)} y2={y(t)} stroke="var(--color-line)" />
              <text
                x={pad.left - 6}
                y={y(t)}
                dy="0.32em"
                textAnchor="end"
                className="fill-fg-subtle text-[11px] tabular-nums"
              >
                {formatY(t)}
              </text>
            </g>
          ))}
          {items.map((it, i) => {
            const cx = pad.left + band * i + band / 2;
            const bh = Math.max(y(0) - y(it.value), it.value > 0 ? 2 : 0);
            const r = Math.min(4, bar / 2, bh);
            const x0 = cx - bar / 2;
            const yTop = y(0) - bh;
            return (
              <g key={it.label}>
                {bh > 0 && (
                  // Ponta arredondada, base reta.
                  <path
                    d={`M${x0},${y(0)}V${yTop + r}Q${x0},${yTop} ${x0 + r},${yTop}H${x0 + bar - r}Q${x0 + bar},${yTop} ${x0 + bar},${yTop + r}V${y(0)}Z`}
                    fill="var(--color-brand-400)"
                    opacity={hover === null || hover === i ? 1 : 0.45}
                  />
                )}
                {i % labelEvery === 0 && (
                  <text x={cx} y={height - 6} textAnchor="middle" className="fill-fg-subtle text-[11px]">
                    {it.label}
                  </text>
                )}
                <rect
                  x={pad.left + band * i}
                  y={pad.top}
                  width={band}
                  height={h}
                  fill="transparent"
                  onPointerEnter={() => setHover(i)}
                  onPointerLeave={() => setHover(null)}
                />
              </g>
            );
          })}
        </svg>
      )}
      {hover !== null && items[hover] && (
        <Tooltip x={pad.left + band * hover + band / 2} width={width}>
          <span className="block text-sm font-semibold tabular-nums">{formatY(items[hover]!.value)}</span>
          <span className="block text-xs text-fg-subtle">{items[hover]!.tooltip ?? items[hover]!.label}</span>
        </Tooltip>
      )}
    </div>
  );
}

/** Barras horizontais com rótulo e valor — participação por categoria. */
export function BarList({
  items,
  formatValue = (v) => v.toLocaleString('pt-BR'),
}: {
  items: { key: string; label: ReactNode; value: number; percent: number }[];
  formatValue?: (v: number) => string;
}) {
  const max = Math.max(...items.map((i) => i.percent), 1);
  return (
    <ul className="space-y-3">
      {items.map((it) => (
        <li key={it.key}>
          <div className="mb-1 flex items-baseline justify-between gap-3 text-sm">
            <span className="min-w-0 truncate text-fg-muted">{it.label}</span>
            <span className="shrink-0 tabular-nums">
              <span className="font-medium">{it.percent.toLocaleString('pt-BR')}%</span>
              <span className="ml-2 text-xs text-fg-subtle">{formatValue(it.value)}</span>
            </span>
          </div>
          <div className="h-2 overflow-hidden rounded-full bg-surface-2">
            <div
              className="h-full rounded-full bg-brand-400"
              style={{ width: `${(it.percent / max) * 100}%` }}
            />
          </div>
        </li>
      ))}
    </ul>
  );
}

function Tooltip({ x, width, children }: { x: number; width: number; children: ReactNode }) {
  // Não sai pelas bordas do gráfico.
  const left = Math.min(Math.max(x, 70), Math.max(width - 70, 70));
  return (
    <div
      role="status"
      className={cn(
        'glass-strong pointer-events-none absolute top-0 z-10 -translate-x-1/2 rounded-xl px-3 py-2 whitespace-nowrap',
      )}
      style={{ left }}
    >
      {children}
    </div>
  );
}
